import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';
import { Calendar, Clock, User, Phone, Mail, MapPin, AlertCircle, CheckCircle, XCircle, Loader2, CalendarClock } from 'lucide-react';

import apiClient from '@/lib/api-client';
import { useCurrency } from '@/hooks/useCurrency';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LanguagePill } from '@/components/chrome/LanguagePill';

interface GuestBooking {
  id: number;
  status: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_full_name: string;
  customer_email: string;
  customer_phone: string;
  notes: string;
  start_time: string;
  end_time: string;
  confirmed_at: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  service: {
    id: number;
    name: string;
    description: string;
    duration_minutes: number;
    price: number;
  };
  provider: {
    id: number;
    name: string;
    title: string;
    bio: string;
  };
  organization: {
    id: number;
    name: string;
    email: string;
    phone: string;
    address: string;
  };
  hide_price?: boolean;
  token_expires_at: string;
  created_at: string;
  updated_at: string;
}

interface GuestBookingResponse {
  booking: GuestBooking;
  can_cancel: boolean;
  can_reschedule: boolean;
}

export default function GuestBookingDetailsPage() {
  const { token } = useParams<{ token: string }>();
  const [_, setLocation] = useLocation();
  const { formatCurrency } = useCurrency();
  const { t } = useTranslation('guestBooking');

  // Fetch booking details using guest token
  const { data, isLoading, error } = useQuery<GuestBookingResponse>({
    queryKey: ['guest-booking', token],
    queryFn: async () => {
      const response = await apiClient.get(`/v1/guest/bookings/${token}`);
      return response.data.data;
    },
    enabled: !!token,
    retry: false,
  });

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-surface-2 text-ink border-line';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
              <p className="text-soft">{t('details.loading')}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
              <div>
                <h3 className="text-h3 text-ink">{t('details.bookingNotFound')}</h3>
                <p className="text-soft">
                  {t('details.bookingNotFoundDescription')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { booking, can_cancel, can_reschedule } = data;
  const startTime = parseISO(booking.start_time);
  const endTime = parseISO(booking.end_time);
  const tokenExpires = parseISO(booking.token_expires_at);

  return (
    <div className="min-h-screen bg-surface-2 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Language Selector */}
        <div className="flex justify-end">
          <LanguagePill />
        </div>

        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-h1 text-ink">{booking.organization.name}</h1>
          <p className="text-soft">{t('details.subtitle')}</p>
        </div>

        {/* Status Alert */}
        {booking.status === 'cancelled' && (
          <Alert className="border-red-200 bg-red-50">
            <XCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              {t('details.cancelledAlert')}
              {booking.cancellation_reason && ` ${t('details.cancelledReason', { reason: booking.cancellation_reason })}`}
              {booking.cancelled_at && (
                <span className="block text-sm mt-1">
                  {t('details.cancelledOn', {
                    date: format(parseISO(booking.cancelled_at), getDateFormats().full, { locale: getDateLocale() }),
                    time: format(parseISO(booking.cancelled_at), 'HH:mm'),
                  })}
                </span>
              )}
            </AlertDescription>
          </Alert>
        )}

        {booking.status === 'confirmed' && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              {t('details.confirmedAlert')}
            </AlertDescription>
          </Alert>
        )}

        {/* Main Details Card */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <CardTitle className="text-2xl">{booking.service.name}</CardTitle>
                <Badge className={getStatusBadgeColor(booking.status)}>
                  {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Appointment Details */}
            <div className="space-y-4">
              <h3 className="text-h3 text-ink">{t('details.appointmentDetails')}</h3>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-soft mt-0.5" />
                    <div>
                      <p className="font-medium">{t('details.date')}</p>
                      <p className="text-soft">
                        {format(startTime, getDateFormats().fullWithWeekday, { locale: getDateLocale() })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-soft mt-0.5" />
                    <div>
                      <p className="font-medium">{t('details.time')}</p>
                      <p className="text-soft">
                        {format(startTime, 'HH:mm')} - {format(endTime, 'HH:mm')}
                      </p>
                      <p className="text-sm text-soft">
                        {t('details.duration', { minutes: booking.service.duration_minutes })}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <User className="h-5 w-5 text-soft mt-0.5" />
                    <div>
                      <p className="font-medium">{t('details.provider')}</p>
                      <p className="text-soft">{booking.provider.name}</p>
                      {booking.provider.title && (
                        <p className="text-sm text-soft">{booking.provider.title}</p>
                      )}
                    </div>
                  </div>
                  {booking.service.price && !booking.hide_price && (
                    <div className="flex items-start gap-3">
                      <div className="h-5 w-5 text-soft mt-0.5 flex items-center justify-center">
                        $
                      </div>
                      <div>
                        <p className="font-medium">{t('details.price')}</p>
                        <p className="text-soft">{formatCurrency(booking.service.price || 0)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Separator />

            {/* Service Description */}
            {booking.service.description && (
              <>
                <div className="space-y-2">
                  <h3 className="text-h3 text-ink">{t('details.aboutService')}</h3>
                  <p className="text-soft">{booking.service.description}</p>
                </div>
                <Separator />
              </>
            )}

            {/* Customer Notes */}
            {booking.notes && (
              <>
                <div className="space-y-2">
                  <h3 className="text-h3 text-ink">{t('details.yourNotes')}</h3>
                  <p className="text-soft">{booking.notes}</p>
                </div>
                <Separator />
              </>
            )}

            {/* Contact Information */}
            <div className="space-y-2">
              <h3 className="text-h3 text-ink">{t('details.contactInformation')}</h3>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-soft" />
                  <a
                    href={`mailto:${booking.organization.email}`}
                    className="text-sm text-primary hover:underline"
                  >
                    {booking.organization.email}
                  </a>
                </div>
                {booking.organization.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-soft" />
                    <a
                      href={`tel:${booking.organization.phone}`}
                      className="text-sm text-primary hover:underline"
                    >
                      {booking.organization.phone}
                    </a>
                  </div>
                )}
                {booking.organization.address && (
                  <div className="flex items-start gap-3 md:col-span-2">
                    <MapPin className="h-4 w-4 text-soft mt-0.5" />
                    <p className="text-sm text-soft">{booking.organization.address}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            {(can_cancel || can_reschedule) && booking.status !== 'cancelled' && (
              <>
                <Separator />
                <div className="flex flex-col sm:flex-row gap-3">
                  {can_reschedule && (
                    <Button
                      className="flex-1 w-full"
                      onClick={() => setLocation(`/guest/bookings/${token}/reschedule`)}
                    >
                      <CalendarClock className="h-4 w-4 mr-2" />
                      {t('details.rescheduleAppointment')}
                    </Button>
                  )}
                  {can_cancel && (
                    <Button
                      variant="outline-danger"
                      className="flex-1 w-full"
                      onClick={() => setLocation(`/guest/bookings/${token}/cancel`)}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      {t('details.cancelAppointment')}
                    </Button>
                  )}
                </div>
              </>
            )}

            {/* Link Expiration Notice */}
            <div className="text-xs text-soft text-center pt-4">
              {t('details.linkExpires', { date: format(tokenExpires, getDateFormats().full, { locale: getDateLocale() }) })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
